/**
 * Lightweight HTML sanitizer used by the CRM RichNotes editor.
 *
 * The notes editor only ever needs:
 *   - Plain text + line breaks (<br>, <div>, <p>, <span>)
 *   - Inline screenshots as <img> with a data: or https: src
 *
 * Anything else is stripped (script tags, event handlers, javascript: hrefs,
 * iframes, etc.). This prevents stored-XSS via pasted content.
 *
 * Implementation parses the HTML with DOMParser (no execution) and walks the
 * tree rejecting/cleaning nodes by allow-list.
 */

const ALLOWED_TAGS = new Set(["BR", "DIV", "P", "SPAN", "IMG", "B", "I", "EM", "STRONG", "UL", "OL", "LI"]);
const ALLOWED_ATTRS = new Set(["src", "alt", "class"]);
const SAFE_PROTOCOLS = /^(data:image\/(png|jpe?g|webp|gif);base64,|https?:\/\/)/i;

export function sanitizeRichNotes(html) {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";
  walk(root);
  return root.innerHTML;
}

function walk(node) {
  // Iterate over children copy because we may remove nodes.
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) continue; // text node ok
    if (child.nodeType !== 1) { child.remove(); continue; }
    const tag = child.tagName.toUpperCase();
    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap unknown tags so text isn't lost (but reject <script> etc.).
      if (tag === "SCRIPT" || tag === "IFRAME" || tag === "OBJECT" || tag === "EMBED" || tag === "STYLE") {
        child.remove();
        continue;
      }
      // unwrap
      while (child.firstChild) node.insertBefore(child.firstChild, child);
      child.remove();
      continue;
    }
    // Strip every attribute that's not allow-listed.
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      if (!ALLOWED_ATTRS.has(name)) { child.removeAttribute(attr.name); continue; }
      // img src must be data:image/... or https?:
      if (tag === "IMG" && name === "src" && !SAFE_PROTOCOLS.test(attr.value || "")) {
        child.removeAttribute(attr.name);
      }
    }
    // Recurse
    walk(child);
  }
}
